import * as React from 'react';

import 'dayjs/locale/ja';
import { FormHelperText, TextField, Typography } from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

import { black, gray, grayInput, primarycolor } from '../../constants/Colors';
import {
  formLabelWrapper,
  formNarrowMb,
  formExtraNarrowMb,
  formWideMb
} from '../../constants/Location';

import type { SxProps } from '@mui/material';
import type { Dayjs } from 'dayjs';
import type { FieldError, FieldErrorsImpl, Merge } from 'react-hook-form';

interface Props {
  labelText?: string;
  requiredText?: string;
  isRequired: boolean;
  error?: boolean;
  helperText?:
    | string
    | FieldError
    | Merge<FieldError, FieldErrorsImpl<any>>
    | undefined;
  fieldType?: string;
  width?: string;
  spWidth?: string;
  formMb?: string | undefined;
  placeholder?: string;
  disabled?: boolean;
  disabledColor?: string;
  value: Dayjs | null | undefined;
  setValue: (
    value: Dayjs | null,
    keyboardInputValue?: string | undefined
  ) => void;
  onChangeForValide?: VoidFunction;
  flex?: number;
  twoComponent?: boolean;
}

const CustomDatePicker = React.forwardRef(
  (props: Props): React.ReactElement | JSX.Element => {
    const locale = 'ja' as const;
    const {
      labelText,
      requiredText,
      isRequired,
      error,
      helperText,
      formMb,
      width,
      disabled,
      disabledColor,
      setValue,
      value,
      flex,
      twoComponent,
      ...rest
    } = props;

    const theme = createTheme({
      components: {
        MuiOutlinedInput: {
          styleOverrides: {
            root: {
              '& .MuiSvgIcon-root': {
                color: disabled && disabledColor ? black : 'inherit'
              }
            }
          }
        }
      }
    });

    function inputBorderFocusColor(): string {
      if (error) {
        return primarycolor;
      }
      return black;
    }

    function inputBorderColor(): string {
      if (error) {
        return primarycolor;
      }
      return grayInput;
    }

    let inputWidth;
    let inputSpWidth;
    if (width === 'search') {
      inputWidth = '152px';
      inputSpWidth = '134px';
    } else {
      inputWidth = width;
      inputSpWidth = width;
    }

    const styles = {
      datePicker: {
        '& .MuiOutlinedInput-root': {
          '& fieldset': {
            borderColor: inputBorderColor()
          },
          '&.Mui-focused fieldset': {
            borderColor: inputBorderFocusColor()
          },
          '&:hover fieldset': {
            borderColor: inputBorderFocusColor()
          }
        },
        '@media screen and (max-width:480px)': {
          width: inputSpWidth ?? '148px'
        },
        width: inputWidth ?? '180px'
      },
      formLabelWrapper,
      label: {
        fontSize: '12px'
      },
      required: {
        color: primarycolor,
        fontSize: '12px',
        marginLeft: '10px'
      }
    };

    const inputformMb = (marginBottom: string | undefined): SxProps => {
      if (marginBottom === 'zero') return formExtraNarrowMb;
      if (marginBottom === 'narrow') return formNarrowMb;
      return formWideMb;
    };

    const textfieldHelper = (): string | undefined => {
      if (helperText === undefined) return;
      return String(helperText);
    };

    return (
      <Typography
        flex={flex}
        sx={inputformMb(formMb)}
        align="left"
        component="div"
      >
        <Typography align="left" sx={styles.formLabelWrapper} component="div">
          {labelText ? (
            <Typography color={gray} sx={styles.label} component="span">
              {labelText}
            </Typography>
          ) : (
            ''
          )}
          {isRequired ? (
            <Typography component="span" sx={styles.required}>
              {requiredText}
            </Typography>
          ) : (
            ''
          )}
        </Typography>
        <ThemeProvider theme={theme}>
          <LocalizationProvider
            dateAdapter={AdapterDayjs}
            adapterLocale={locale}
          >
            <DatePicker
              orientation="portrait"
              renderInput={(params) => (
                <TextField
                  {...params}
                  inputProps={{
                    ...params.inputProps,
                    disabled,
                    style:
                      disabled && disabledColor
                        ? { WebkitTextFillColor: black }
                        : {}
                  }}
                  size="small"
                  sx={styles.datePicker}
                />
              )}
              inputFormat="YYYY/MM/DD"
              onChange={setValue}
              value={value}
              disabled={disabled}
              {...rest}
            />
          </LocalizationProvider>
        </ThemeProvider>
        {twoComponent ? (
          <Typography sx={{ height: '20px' }}>
            <FormHelperText error={error}>{textfieldHelper()}</FormHelperText>
          </Typography>
        ) : (
          <FormHelperText error={error}>{textfieldHelper()}</FormHelperText>
        )}
      </Typography>
    );
  }
);
CustomDatePicker.displayName = 'CustomDatePicker';
export default CustomDatePicker;
